import { Box, Button, Icon, Tooltip } from '@auviknetworks/iceberg-ui';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

const FormRepeatedContainer = ({
    children,
    disabled,
    max = 20,
    min = 1,
    prefix,
    values,
    showButtons = true
}) => {
    const { control, unregister } = useFormContext();
    const { name, label, showLabel } = children.props;
    const { t } = useTranslation();
    const childrenValues = useMemo(
        () => (values ? values[name] : {}),
        [values, name]
    );

    const { fields, insert, remove } = useFieldArray({
        control,
        name: 'count'
    });

    const insertField = useCallback(
        (index) => {
            insert(
                index,
                {},
                {
                    shouldFocus: false
                }
            );
        },
        [insert]
    );

    const removeField = useCallback(
        (index) => {
            remove(index);
            unregister();
        },
        [remove, unregister]
    );

    function renderFields(field, index, fieldsArray) {
        const isFirst = index === 0;
        const showTrashButton = fieldsArray.length > min;
        const newLabel = isFirst || showLabel ? label : '';
        const fieldName = `${prefix}-${index}-${name}`;

        const newProps = {
            label: newLabel,
            name: fieldName,
            value: field[name]
        };

        return (
            <RepeatedGroupContainer
                key={field.id}
                data-testid={`RepeatedGroup.${index}.${prefix}.${name}`}
            >
                <Box flex="1">{React.cloneElement(children, newProps)}</Box>

                {showButtons && (
                    <ButtonContainer numButtons={2}>
                        {showTrashButton && (
                            <Tooltip
                                content={t('Common.delete')}
                                placement="top"
                            >
                                <TrashButton
                                    disabled={disabled}
                                    onClick={() =>
                                        removeField(index, fieldName)
                                    }
                                    size="noPadding"
                                    textStyle="greyIcon"
                                    type="button"
                                    variant="iconWithoutBorder"
                                >
                                    <Icon size="16px" src="Trash" />
                                </TrashButton>
                            </Tooltip>
                        )}

                        {max && fieldsArray.length < max && (
                            <Tooltip content={t('Common.add')} placement="top">
                                <AddButton
                                    disabled={disabled}
                                    onClick={() => insertField(index + 1)}
                                    size="noPadding"
                                    textStyle="greyIcon"
                                    type="button"
                                    variant="iconWithoutBorder"
                                >
                                    <Icon size="13px" src="Add" />
                                </AddButton>
                            </Tooltip>
                        )}
                    </ButtonContainer>
                )}
            </RepeatedGroupContainer>
        );
    }

    // Create fields if we have fewer than the specified `min`
    useEffect(() => {
        const missingFields = min - fields.length;
        if (missingFields <= 0) {
            return;
        }

        if (childrenValues && childrenValues.length > 0) {
            childrenValues.map((value, idx) => insertField(idx, value));
            return;
        }
        for (let i = 0; i < missingFields; i += 1) {
            insertField(fields.length + i);
        }
    }, [childrenValues, min, fields, insertField]);

    return fields.map(renderFields);
};

export const FormRepeatedGroup = ({ children, prefix, values }) => {
    const childrenArray = Array.isArray(children) ? children : [children];
    return childrenArray.map((child, index) => {
        const { props } = child;
        return (
            <FormRepeatedContainer
                prefix={prefix}
                values={values}
                {...props}
                // eslint-disable-next-line react/no-array-index-key
                key={`FormRepeatedContainer.${index}`}
            >
                {child}
            </FormRepeatedContainer>
        );
    });
};

const RepeatedGroupContainer = styled(Box)`
    gap: 4px;
    display: flex;
    align-items: flex-start;
    margin-bottom: 4px;
`;

const ButtonContainer = styled.div`
    gap: 5px;
    height: 24px;
    display: inline-grid;
    padding-top: 8px;
    grid-template-rows: 1fr;
    grid-template-columns: ${({ numButtons }) =>
        numButtons === 2 ? '16px 16px' : '16px 16px 16px 16px'};
    align-self: flex-end;
    margin-bottom: 24px;
    margin-left: 4px;
`;

const TrashButton = styled(Button)`
    grid-column: 1/1;
`;

const AddButton = styled(Button)`
    grid-column: 2/2;
`;
